import React, { useState, useEffect } from 'react'
import { GraduationCap, Calendar, Clock, Users, DollarSign, AlertCircle, CheckCircle, XCircle, Search, X, ChevronDown } from 'lucide-react'
import Button from '../../components/common/Button'
import Card from '../../components/common/Card'
import Modal from '../../components/common/Modal'
import Input from '../../components/common/Input'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import toast from 'react-hot-toast'
import { inscripcionService } from '../../services/inscripcionService'
import { estudianteService } from '../../services/estudianteService'
import { useForm } from 'react-hook-form'

const RegistroInscripciones = () => {
  const [programas, setProgramas] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [selectedPrograma, setSelectedPrograma] = useState(null)
  const [selectedGrupo, setSelectedGrupo] = useState(null)
  const [conflictos, setConflictos] = useState([])
  const [verificando, setVerificando] = useState(false)
  const [verificacionResultado, setVerificacionResultado] = useState(null)
  const [descuentosVigentes, setDescuentosVigentes] = useState([])
  const [cargandoDescuentos, setCargandoDescuentos] = useState(false)
  
  // Estados para buscador de estudiantes
  const [busquedaEstudiante, setBusquedaEstudiante] = useState('')
  const [estudiantesFiltrados, setEstudiantesFiltrados] = useState([])
  const [mostraBuscador, setMostraBuscador] = useState(false)
  const [cargandoEstudiantes, setCargandoEstudiantes] = useState(false)
  const [estudianteSeleccionado, setEstudianteSeleccionado] = useState(null)
  const [inscripcionesActivas, setInscripcionesActivas] = useState([])
  const [programasExpandidos, setProgramasExpandidos] = useState({})

  const { register, handleSubmit, reset, watch, formState: { errors } } = useForm({
    defaultValues: {
      numero_cuotas: 3,
      descuento_id: ''
    }
  })

  const numeroCuotas = watch('numero_cuotas')
  const descuentoSeleccionado = watch('descuento_id')

  useEffect(() => {
    fetchProgramas()
  }, [])

  // Buscar estudiantes mientras escriben
  useEffect(() => {
    if (busquedaEstudiante.trim().length >= 2) {
      buscarEstudiantes()
    } else {
      setEstudiantesFiltrados([])
    }
  }, [busquedaEstudiante])

  const fetchProgramas = async () => {
    try {
      setLoading(true)
      const response = await inscripcionService.getProgramasDisponiblesAdmin()
      
      if (response.success) {
        setProgramas(response.data || [])
      } else {
        // Si el error es por documentos no aprobados, mostrar mensaje específico
        if (response.message?.includes('documentos aprobados')) {
          toast.error('Debes tener tus documentos aprobados para poder inscribirte. Completa la verificación de documentos primero.')
        } else {
          toast.error(response.message || 'Error al cargar programas')
        }
        setProgramas([])
      }
    } catch (error) {
      // Verificar si el error es por documentos no aprobados
      if (error.response?.data?.message?.includes('documentos aprobados')) {
        toast.error('Debes tener tus documentos aprobados para poder inscribirte. Completa la verificación de documentos primero.')
      } else {
        toast.error('Error de conexión')
      }
      setProgramas([])
    } finally {
      setLoading(false)
    }
  }

  const buscarEstudiantes = async () => {
    try {
      setCargandoEstudiantes(true)
      const response = await estudianteService.buscar(busquedaEstudiante)
      
      if (response.success) {
        setEstudiantesFiltrados(response.data || [])
      } else {
        setEstudiantesFiltrados([])
      }
    } catch (error) {
      console.error('Error buscando estudiantes:', error)
      setEstudiantesFiltrados([])
    } finally {
      setCargandoEstudiantes(false)
    }
  }

  const seleccionarEstudiante = async (estudiante) => {
    setEstudianteSeleccionado(estudiante)
    setBusquedaEstudiante(`${estudiante.nombre} ${estudiante.apellido}`)
    setEstudiantesFiltrados([])
    setMostraBuscador(false)
    
    // Cargar inscripciones activas del estudiante
    try {
      const response = await inscripcionService.getMisInscripcionesPorEstudiante(estudiante.id)
      if (response.success) {
        setInscripcionesActivas(response.data || [])
      }
    } catch (error) {
      console.error('Error al cargar inscripciones:', error)
    }
  }

  const limpiarEstudiante = () => {
    setEstudianteSeleccionado(null)
    setBusquedaEstudiante('')
    setEstudiantesFiltrados([])
  }

  const fetchDescuentosVigentes = async (programaId = null) => {
    try {
      setCargandoDescuentos(true)
      const response = await inscripcionService.getDescuentosVigentes(programaId)
      
      if (response.success) {
        setDescuentosVigentes(response.data || [])
      } else {
        setDescuentosVigentes([])
      }
    } catch (error) {
      console.error('Error al cargar descuentos:', error)
      setDescuentosVigentes([])
    } finally {
      setCargandoDescuentos(false)
    }
  }

  const handleInscribirse = async (programa) => {
    setSelectedPrograma(programa)
    setSelectedGrupo(null)
    setConflictos([])
    setVerificacionResultado(null)
    reset({
      programa_id: programa.id,
      numero_cuotas: 10,
      descuento_id: ''
    })
    
    // Cargar descuentos vigentes para este programa
    await fetchDescuentosVigentes(programa.id)
    
    // Verificar conflictos de horario para todos los grupos del programa
    setVerificando(true)
    try {
      const verificacion = await inscripcionService.verificarHorariosProgramaAdmin(programa.id, estudianteSeleccionado?.id)
      
      if (verificacion.success) {
        setVerificacionResultado(verificacion)
        if (verificacion.tieneConflictos) {
          toast.error(`Se encontraron conflictos en ${verificacion.total_grupos_con_conflicto} de ${verificacion.total_grupos_programa} grupos`)
        }
        setShowModal(true)
      } else {
        toast.error(verificacion.message || 'Error al verificar horarios')
      }
    } catch (error) {
      console.error('Error verificando horarios:', error)
      toast.error('Error al verificar horarios. Intenta nuevamente.')
    } finally {
      setVerificando(false)
    }
  }

  const onSubmit = async (data) => {
    if (verificacionResultado?.tieneConflictos && verificacionResultado?.total_grupos_disponibles === 0) {
      toast.error('No puede inscribirse. Todos los grupos del programa tienen conflictos de horario')
      return
    }

    try {
      setLoading(true)
      const payload = {
        programa_id: selectedPrograma.id,
        numero_cuotas: parseInt(data.numero_cuotas),
        estudiante_id: estudianteSeleccionado?.id
      }
      
      // Incluir descuento si está seleccionado
      if (data.descuento_id) {
        payload.descuento_id = parseInt(data.descuento_id)
      }
      
      const response = await inscripcionService.crearInscripcionAdmin(payload)
      
      if (response.success) {
        // Crear mensaje con resumen de módulos inscritos
        const modulosInfo = selectedPrograma.modulos?.length 
          ? ` (${selectedPrograma.modulos.length} módulos)` 
          : ''
        const gruposInfo = verificacionResultado?.total_grupos_disponibles 
          ? ` - Inscrito en ${verificacionResultado.total_grupos_disponibles} grupo(s)` 
          : ''
        
        toast.success(`✅ Inscripción realizada exitosamente${modulosInfo}${gruposInfo}`)
        setShowModal(false)
        setSelectedPrograma(null)
        setSelectedGrupo(null)
        setConflictos([])
        setVerificacionResultado(null)
        reset()
        
        // Recargar programas e inscripciones
        await fetchProgramas()
        if (estudianteSeleccionado?.id) {
          const inscResponse = await inscripcionService.getMisInscripcionesPorEstudiante(estudianteSeleccionado.id)
          if (inscResponse.success) {
            setInscripcionesActivas(inscResponse.data || [])
          }
        }
      } else {
        toast.error(response.message || 'Error al realizar inscripción')
        if (response.errors) {
          Object.keys(response.errors).forEach(key => {
            toast.error(`${key}: ${response.errors[key]}`)
          })
        }
      }
    } catch (error) {
      toast.error('Error al realizar inscripción')
    } finally {
      setLoading(false)
    }
  }

  const toggleProgramaExpanded = (programaId) => {
    setProgramasExpandidos(prev => ({
      ...prev,
      [programaId]: !prev[programaId]
    }))
  }

  const formatHorario = (horario) => {
    if (!horario) return '-'
    return `${horario.dias} ${horario.hora_ini} - ${horario.hora_fin}`
  }

  if (loading && programas.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center space-x-4">
        <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-accent-500 rounded-2xl flex items-center justify-center shadow-glow">
          <GraduationCap className="h-6 w-6 text-white" />
        </div>
        <div>
          <h1 className="text-3xl font-bold gradient-text">Inscripciones</h1>
          <p className="text-gray-600 dark:text-gray-400">Inscríbete en los programas académicos disponibles</p>
        </div>
      </div>

      {/* Buscador de Estudiantes */}
      <Card className="gradient" shadow="glow-lg">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
            <Search className="h-5 w-5" />
            Buscar Estudiante
          </h3>
        </div>

        <div className="relative mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Busca por nombre, cédula o número de documento..."
              value={busquedaEstudiante}
              onChange={(e) => {
                setBusquedaEstudiante(e.target.value)
                setMostraBuscador(true)
              }}
              onFocus={() => setMostraBuscador(true)}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
            />
            {busquedaEstudiante && (
              <button
                onClick={() => limpiarEstudiante()}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>

          {/* Dropdown de autocompletado */}
          {mostraBuscador && (busquedaEstudiante.length >= 2 || estudiantesFiltrados.length > 0) && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
              {cargandoEstudiantes ? (
                <div className="p-4 text-center">
                  <div className="inline-block">
                    <LoadingSpinner size="sm" />
                  </div>
                </div>
              ) : estudiantesFiltrados.length > 0 ? (
                <ul>
                  {estudiantesFiltrados.map((estudiante) => (
                    <li key={estudiante.id}>
                      <button
                        onClick={() => seleccionarEstudiante(estudiante)}
                        className="w-full text-left px-4 py-3 hover:bg-gray-100 dark:hover:bg-gray-700 border-b border-gray-200 dark:border-gray-700 last:border-b-0 transition-colors"
                      >
                        <div className="font-semibold text-gray-900 dark:text-gray-100">
                          {estudiante.nombre} {estudiante.apellido}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          Cédula: {estudiante.ci} | Doc: {estudiante.numero_documento}
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : busquedaEstudiante.length >= 2 ? (
                <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                  No se encontraron estudiantes
                </div>
              ) : null}
            </div>
          )}
        </div>

        {/* Panel de datos del estudiante seleccionado */}
        {estudianteSeleccionado && (
          <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <div className="flex justify-between items-start mb-4">
              <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                Datos del Estudiante
              </h4>
              <button
                onClick={() => limpiarEstudiante()}
                className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-gray-600 dark:text-gray-400 uppercase tracking-wide">Nombre</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {estudianteSeleccionado.nombre} {estudianteSeleccionado.apellido}
                </p>
              </div>

              <div>
                <p className="text-xs text-gray-600 dark:text-gray-400 uppercase tracking-wide">Cédula de Identidad</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {estudianteSeleccionado.ci}
                </p>
              </div>

              <div>
                <p className="text-xs text-gray-600 dark:text-gray-400 uppercase tracking-wide">Número de Documento</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {estudianteSeleccionado.numero_documento}
                </p>
              </div>

              <div>
                <p className="text-xs text-gray-600 dark:text-gray-400 uppercase tracking-wide">Sexo</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {estudianteSeleccionado.sexo === 'M' ? 'Masculino' : estudianteSeleccionado.sexo === 'F' ? 'Femenino' : estudianteSeleccionado.sexo}
                </p>
              </div>

              <div>
                <p className="text-xs text-gray-600 dark:text-gray-400 uppercase tracking-wide">Fecha de Nacimiento</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {new Date(estudianteSeleccionado.fecha_nacimiento).toLocaleDateString()}
                </p>
              </div>

              <div>
                <p className="text-xs text-gray-600 dark:text-gray-400 uppercase tracking-wide">Celular</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {estudianteSeleccionado.celular}
                </p>
              </div>

              <div>
                <p className="text-xs text-gray-600 dark:text-gray-400 uppercase tracking-wide">Dirección</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {estudianteSeleccionado.direccion}
                </p>
              </div>
              {estudianteSeleccionado.registro_estudiante && (
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 uppercase tracking-wide">Registro de Estudiante</p>
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {estudianteSeleccionado.registro_estudiante}
                  </p>
                </div>
              )}

              {inscripcionesActivas.length > 0 && (
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 uppercase tracking-wide">Programa</p>
                  <span className="px-3 py-1 bg-green-500 text-white text-xs font-bold rounded-full flex items-center gap-1">
                    ✅ Inscrito: {inscripcionesActivas[0].programa.nombre }
                  </span>
                </div>
                      
                    )}
            </div>
          </div>
        )}
      </Card>

      {programas.length === 0 ? (
        <Card className="gradient" shadow="glow-lg">
          <div className="text-center py-12">
            <GraduationCap className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              {loading ? 'Cargando programas...' : 'No hay programas disponibles'}
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              {loading 
                ? 'Por favor espera...' 
                : 'Actualmente no hay programas con grupos disponibles para inscripción. Verifica que tus documentos estén aprobados.'}
            </p>
          </div>
        </Card>
      ) : (
        <div className="space-y-6">
          {programas.map((programa) => {
            const estaInscrito = inscripcionesActivas.some(insc => insc.programa_id === programa.id)
            
            return (
            <Card 
              key={programa.id} 
              className={estaInscrito ? "gradient bg-green-50 dark:bg-green-900/10" : "gradient"} 
              shadow="glow-lg"
            >
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-6">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <button 
                      onClick={() => toggleProgramaExpanded(programa.id)}
                      className="transition-transform duration-200"
                      style={{ transform: programasExpandidos[programa.id] ? 'rotate(180deg)' : 'rotate(0deg)' }}
                    >
                      <ChevronDown className="h-6 w-6 text-gray-600 dark:text-gray-400" />
                    </button>
                    <h3 className="text-2xl font-bold gradient-text">{programa.nombre}</h3>
                    {estaInscrito && (
                      <span className="px-3 py-1 bg-green-500 text-white text-xs font-bold rounded-full flex items-center gap-1">
                        ✅ Inscrito
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm text-gray-600 dark:text-gray-400">
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4" />
                      <span>Costo: {parseFloat(programa.costo || 0).toLocaleString('es-BO', { style: 'currency', currency: 'BOB' })}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      <span>Duración: {programa.duracion_meses || 0} meses</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      <span>{programa.total_grupos} grupo(s) disponible(s)</span>
                    </div>
                  </div>
                  {programa.institucion && (
                    <p className="text-sm text-gray-500 mt-2">Institución: {programa.institucion}</p>
                  )}
                </div>
                <Button
                  variant={estaInscrito ? "outline" : "primary"}
                  size="sm"
                  onClick={() => handleInscribirse(programa)}
                  disabled={estaInscrito}
                >
                  {estaInscrito ? 'Ya Inscrito' : 'Inscribirse'}
                </Button>
              </div>

              {/* Sección de Grupos Disponibles (Expandible) */}
              {programasExpandidos[programa.id] && programa.grupos_disponibles && programa.grupos_disponibles.length > 0 && (
                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-900 dark:text-gray-100">Grupos Disponibles:</h4>
                  {programa.grupos_disponibles.map((grupo) => (
                    <div
                      key={grupo.id}
                      className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                            <h5 className="font-semibold text-gray-900 dark:text-gray-100">
                              {`${grupo.modulo}-${grupo.nombre}` || 'Grupo'}
                            </h5>
                            {grupo.cupos_disponibles > 0 && (
                              <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full">
                                {grupo.cupos_disponibles} cupos disponibles
                              </span>
                            )}
                          </div>
                          {grupo.docente && (
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                              Docente: {grupo.docente}
                            </p>
                          )}
                          <div className="space-y-1">
                            {grupo.horarios && grupo.horarios.length > 0 ? (
                              grupo.horarios.map((horario, idx) => (
                                <div key={idx} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                  <Clock className="h-4 w-4" />
                                  <span>{formatHorario(horario)}</span>
                                  {horario.aula && <span className="text-xs">({horario.aula})</span>}
                                </div>
                              ))
                            ) : (
                              <p className="text-sm text-gray-500">Sin horarios asignados</p>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-2">
                            {new Date(grupo.fecha_ini).toLocaleDateString()} - {new Date(grupo.fecha_fin).toLocaleDateString()}
                          </p>
                        </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          )}
          )}
        </div>
      )}

      {/* Modal de Inscripción */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false)
          setSelectedPrograma(null)
          setSelectedGrupo(null)
          setConflictos([])
          setVerificacionResultado(null)
          reset()
        }}
        title="Confirmar Inscripción"
        size="lg"
      >
        {selectedPrograma && (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                {selectedPrograma.nombre}
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                Estudiante: {estudianteSeleccionado?.nombre} {estudianteSeleccionado?.apellido}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                Costo: {parseFloat(selectedPrograma.costo || 0).toLocaleString('es-BO', { style: 'currency', currency: 'BOB' })}
              </p>

              {/* Módulos/Materias del Programa */}
              {selectedPrograma.modulos && selectedPrograma.modulos.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-300 dark:border-gray-600">
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
                    📚 Módulos/Materias a Inscribirse:
                  </p>
                  <div className="space-y-2">
                    {selectedPrograma.modulos.map((modulo, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 bg-white dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600">
                        <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                          {idx + 1}
                        </div>
                        <span className="text-sm text-gray-900 dark:text-gray-100">
                          {modulo.nombre || modulo}
                        </span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-3">
                    Total: {selectedPrograma.modulos.length} módulo(s)
                  </p>
                </div>
              )}
            </div>

            {verificacionResultado && (
              <>
                {/* Resumen de conflictos */}
                <div className={`p-4 rounded-lg border-2 ${
                  verificacionResultado.tieneConflictos 
                    ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-300 dark:border-yellow-800' 
                    : 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-800'
                }`}>
                  <div className="flex items-start gap-3 mb-4">
                    {verificacionResultado.tieneConflictos ? (
                      <AlertCircle className="h-6 w-6 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
                    ) : (
                      <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                    )}
                    <div className="flex-1">
                      <h5 className={`font-bold text-lg mb-2 ${
                        verificacionResultado.tieneConflictos 
                          ? 'text-yellow-900 dark:text-yellow-100' 
                          : 'text-green-900 dark:text-green-100'
                      }`}>
                        {verificacionResultado.tieneConflictos 
                          ? '⚠️ Conflictos Detectados' 
                          : '✅ Sin Conflictos'}
                      </h5>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <p className={`text-xs font-semibold ${
                            verificacionResultado.tieneConflictos 
                              ? 'text-yellow-700 dark:text-yellow-300' 
                              : 'text-green-700 dark:text-green-300'
                          }`}>
                            Total de Grupos
                          </p>
                          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                            {verificacionResultado.total_grupos_programa}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-red-700 dark:text-red-300">
                            Con Conflictos
                          </p>
                          <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                            {verificacionResultado.total_grupos_con_conflicto}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-green-700 dark:text-green-300">
                            Disponibles
                          </p>
                          <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                            {verificacionResultado.total_grupos_disponibles}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Grupos con conflictos */}
                {verificacionResultado.grupos_con_conflictos && verificacionResultado.grupos_con_conflictos.length > 0 && (
                  <div className="space-y-3">
                    <h6 className="font-semibold text-red-900 dark:text-red-100 flex items-center gap-2">
                      <XCircle className="h-4 w-4" />
                      Grupos con Conflictos de Horario ({verificacionResultado.total_grupos_con_conflicto})
                    </h6>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {verificacionResultado.grupos_con_conflictos.map((grupo, idx) => (
                        <div key={idx} className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="font-semibold text-gray-900 dark:text-gray-100">
                                {grupo.modulo} - {grupo.nombre}
                              </p>
                              {grupo.docente && (
                                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                  👨‍🏫 {grupo.docente}
                                </p>
                              )}
                              {grupo.horarios && grupo.horarios.length > 0 && (
                                <div className="mt-2 space-y-1">
                                  {grupo.horarios.map((h, hidx) => (
                                    <p key={hidx} className="text-xs text-gray-700 dark:text-gray-300">
                                      📅 {h.dias || 'Sin día'} {h.hora_ini} - {h.hora_fin}
                                    </p>
                                  ))}
                                </div>
                              )}
                              {grupo.razon_conflicto && (
                                <p className="text-xs text-red-600 dark:text-red-400 mt-2 italic">
                                  {grupo.razon_conflicto}
                                </p>
                              )}
                            </div>
                            <span className="text-xl">❌</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Grupos disponibles */}
                {verificacionResultado.grupos_disponibles && verificacionResultado.grupos_disponibles.length > 0 && (
                  <div className="space-y-3">
                    <h6 className="font-semibold text-green-900 dark:text-green-100 flex items-center gap-2">
                      <CheckCircle className="h-4 w-4" />
                      Grupos Disponibles ({verificacionResultado.total_grupos_disponibles})
                    </h6>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {verificacionResultado.grupos_disponibles.map((grupo, idx) => (
                        <div key={idx} className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="font-semibold text-gray-900 dark:text-gray-100">
                                {grupo.modulo} - {grupo.nombre}
                              </p>
                              {grupo.docente && (
                                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                  👨‍🏫 {grupo.docente}
                                </p>
                              )}
                              {grupo.horarios && grupo.horarios.length > 0 && (
                                <div className="mt-2 space-y-1">
                                  {grupo.horarios.map((h, hidx) => (
                                    <p key={hidx} className="text-xs text-gray-700 dark:text-gray-300">
                                      📅 {h.dias || 'Sin día'} {h.hora_ini} - {h.hora_fin}
                                    </p>
                                  ))}
                                </div>
                              )}
                            </div>
                            <span className="text-xl">✅</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Mensaje informativo */}
                {verificacionResultado.tieneConflictos && (
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <p className="text-sm text-blue-900 dark:text-blue-100">
                      💡 <span className="font-semibold">Nota:</span> {verificacionResultado.total_grupos_disponibles > 0 
                        ? `Puedes inscribirse en los ${verificacionResultado.total_grupos_disponibles} grupo(s) sin conflicto. Los demás grupos no estarán disponibles.`
                        : 'No hay grupos disponibles sin conflictos. Por favor, revisa el horario del estudiante.'}
                    </p>
                  </div>
                )}
              </>
            )}

            {/* Campo de cuotas */}
            {verificacionResultado && verificacionResultado.total_grupos_disponibles > 0 && (
              <>
                {/* Select de Descuentos */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Descuento Disponible (Opcional)
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800 dark:text-gray-100"
                    {...register('descuento_id')}
                    disabled={cargandoDescuentos}
                  >
                    <option value="">Sin descuento</option>
                    {descuentosVigentes.map(desc => (
                      <option key={desc.id} value={desc.id}>
                        {desc.nombre} - {desc.descuento}% de descuento
                      </option>
                    ))}
                  </select>
                  {descuentosVigentes.length === 0 && !cargandoDescuentos && (
                    <p className="text-xs text-gray-500 mt-1">No hay descuentos vigentes disponibles</p>
                  )}
                  {descuentoSeleccionado && (
                    <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                      ✓ Descuento seleccionado: {descuentosVigentes.find(d => d.id.toString() === descuentoSeleccionado)?.descuento}%
                    </p>
                  )}
                </div>

                <Input
                  label="Número de Cuotas *"
                  type="number"
                  min="1"
                  max="12"
                  placeholder="3"
                  error={errors.numero_cuotas?.message}
                  {...register('numero_cuotas', { 
                    required: 'El número de cuotas es obligatorio',
                    min: { value: 1, message: 'Mínimo 1 cuota' },
                    max: { value: 12, message: 'Máximo 12 cuotas' }
                  })}
                />

                {numeroCuotas && selectedPrograma.costo && (
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    {(() => {
                      const costoBase = parseFloat(selectedPrograma.costo)
                      let costoFinal = costoBase
                      let descuentoAplicado = 0

                      if (descuentoSeleccionado) {
                        const descuento = descuentosVigentes.find(d => d.id.toString() === descuentoSeleccionado)
                        if (descuento) {
                          descuentoAplicado = (costoBase * descuento.descuento) / 100
                          costoFinal = costoBase - descuentoAplicado
                        }
                      }

                      const montoPorCuota = costoFinal / parseInt(numeroCuotas)

                      return (
                        <>
                          <div className="space-y-2">
                            <div className="flex justify-between items-center">
                              <p className="text-sm text-gray-600 dark:text-gray-400">Costo Base:</p>
                              <p className="font-semibold text-gray-900 dark:text-gray-100">
                                {costoBase.toLocaleString('es-BO', { style: 'currency', currency: 'BOB' })}
                              </p>
                            </div>

                            {descuentoSeleccionado && descuentoAplicado > 0 && (
                              <>
                                <div className="flex justify-between items-center text-green-600 dark:text-green-400">
                                  <p className="text-sm">Descuento Aplicado:</p>
                                  <p className="font-semibold">
                                    -{descuentoAplicado.toLocaleString('es-BO', { style: 'currency', currency: 'BOB' })}
                                  </p>
                                </div>
                                <div className="border-t border-gray-300 dark:border-gray-600 pt-2">
                                  <div className="flex justify-between items-center">
                                    <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">Costo Final:</p>
                                    <p className="font-bold text-gray-900 dark:text-gray-100">
                                      {costoFinal.toLocaleString('es-BO', { style: 'currency', currency: 'BOB' })}
                                    </p>
                                  </div>
                                </div>
                              </>
                            )}

                            <div className="border-t border-gray-300 dark:border-gray-600 pt-2 mt-3">
                              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Monto por cuota ({numeroCuotas} cuotas):</p>
                              <p className="text-xl font-bold text-blue-600 dark:text-blue-400">
                                {montoPorCuota.toLocaleString('es-BO', { style: 'currency', currency: 'BOB' })}
                              </p>
                            </div>
                          </div>
                        </>
                      )
                    })()}
                  </div>
                )}
              </>
            )}

            {/* Botones de acción */}
            <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowModal(false)
                  setSelectedPrograma(null)
                  setSelectedGrupo(null)
                  setConflictos([])
                  setVerificacionResultado(null)
                  reset()
                }}
              >
                Cerrar
              </Button>
              {verificacionResultado && verificacionResultado.total_grupos_disponibles > 0 && (
                <Button
                  type="submit"
                  variant="primary"
                  icon={<CheckCircle className="h-5 w-5" />}
                  disabled={loading || verificando}
                >
                  {loading ? 'Procesando...' : 'Confirmar Inscripción'}
                </Button>
              )}
            </div>

            {verificacionResultado && verificacionResultado.total_grupos_disponibles === 0 && (
              <div className="p-4 bg-red-50 dark:bg-red-900/20 border-2 border-red-300 dark:border-red-800 rounded-lg text-center">
                <p className="text-red-900 dark:text-red-100 font-semibold">
                  ❌ No se puede incribirse. Todos los grupos tienen conflicto de horario.
                </p>
              </div>
            )}
          </form>
        )}
      </Modal>
    </div>
  )
}

export default RegistroInscripciones

